import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ShoppingCart, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { 
  Product, ProductImage, PRINT_SIZES, getPriceForSize, 
  getSmallestSizePrice, formatPrice, localizeProduct, resolveAssetUrl 
} from "../lib/api";
import { useCartStore } from "../store/cart";
import { useGuestCartStore } from "../store/guestCart";
import { useCurrencyStore } from "../store/currency";
import { useAuthStore } from "../store/auth";
import { useTenant } from "../lib/tenant";
import toast from "react-hot-toast";

const SIZE_LABEL: Record<string, string> = { A6: "A6 (Card)" };

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [lensState, setLensState] = useState<{ clientX: number; clientY: number; rect: DOMRect } | null>(null);
  const zoomLevels = [1, 2, 3];
  const LENS_SIZE = 160;
  const { add, toggleOpen } = useCartStore();
  const guestCart = useGuestCartStore();
  const { user } = useAuthStore();
  const { currency, siteMode } = useCurrencyStore();
  const { id: tenantId, tTenant } = useTenant();
  const { t, i18n } = useTranslation();

  const { data: product, isLoading, error } = useQuery<Product>({
    queryKey: ["product", slug],
    queryFn: () => api.get(`/products/${slug}`).then((r) => r.data),
  });

  const { data: images = [] } = useQuery<ProductImage[]>({
    queryKey: ["product-images", product?.id],
    queryFn: () => api.get(`/products/${product!.id}/images`).then((r) => Array.isArray(r.data) ? r.data : []),
    enabled: !!product?.id,
  });

  const displayImages: string[] =
    images.length > 0
      ? [
          ...images.filter((img) => img.isPrimary),
          ...images.filter((img) => !img.isPrimary),
        ].map((img) => img.url)
      : product?.imageUrl
      ? [product.imageUrl]
      : ["/placeholder.jpg"];

  useEffect(() => {
    setActiveIndex(0);
    setZoom(1);
    if (product) {
      const { size } = getSmallestSizePrice(product, currency);
      setSelectedSize(size);
    }
  }, [product?.id, currency]);

  useEffect(() => { setZoom(1); setLensState(null); }, [activeIndex]);

  function isVideo(url: string) {
    return /\.(mp4|webm|mov|ogg)(\?|$)/i.test(url);
  }

  async function handleAddToCart() {
    if (product?.soldOut) return;
    if (!user) {
      guestCart.add(product!, qty, selectedSize);
      toast.success(tTenant("product.addedToCart"));
      guestCart.toggleOpen();
      return;
    }
    try {
      await add(product!.id, qty, selectedSize);
      toast.success(tTenant("product.addedToCart"));
      toggleOpen();
    } catch {
      toast.error(tTenant("product.failedToAdd"));
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 animate-pulse">
        <div className="flex flex-col gap-3">
          <div className="aspect-square rounded-2xl bg-gray-100" />
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => <div key={i} className="w-16 h-16 rounded-lg bg-gray-100" />)}
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-8 bg-gray-100 rounded w-3/4" />
          <div className="h-5 bg-gray-100 rounded w-1/4" />
          <div className="h-4 bg-gray-100 rounded w-full" />
        </div>
      </div>
    );
  }

  const { name: productName, description: productDescription } =
    product ? localizeProduct(product, i18n.language) : { name: "", description: "" };

  if (error || !product) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 mb-4">Product not found.</p>
        <Link to="/shop" className="btn-secondary">{tTenant("product.backToShop")}</Link>
      </div>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link
        to="/shop"
        className="inline-flex items-center gap-1.5 text-sm mb-8 transition-colors hover:opacity-70 font-medium"
        style={{ color: 'rgb(var(--brand-color))' }}
      >
        <ArrowLeft size={15} /> {tTenant("product.backToShop")}
      </Link>

      <div className="grid md:grid-cols-2 gap-12">
        {/* Gallery */}
        <div className="flex flex-col gap-3">
          <div
            className={`relative aspect-square rounded-2xl overflow-hidden bg-gray-50 shadow-sm group ${zoom > 1 && !isVideo(displayImages[activeIndex]) ? "cursor-none" : ""}`}
            onMouseMove={(e) => {
              if (zoom <= 1 || isVideo(displayImages[activeIndex])) return;
              setLensState({ clientX: e.clientX, clientY: e.clientY, rect: e.currentTarget.getBoundingClientRect() });
            }}
            onMouseLeave={() => setLensState(null)}
          >
            {isVideo(displayImages[activeIndex]) ? (
              <video
                key={displayImages[activeIndex]}
                src={resolveAssetUrl(displayImages[activeIndex])}
                className="h-full w-full object-cover"
                controls
                playsInline
              />
            ) : (
              <img
                src={resolveAssetUrl(displayImages[activeIndex])}
                alt={productName}
                className="h-full w-full object-cover"
                draggable={false}
              />
            )}

            {!isVideo(displayImages[activeIndex]) && (
              <div className="absolute bottom-3 right-3 flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity">
                {zoomLevels.map((z) => (
                  <button
                    key={z}
                    onClick={() => setZoom(z)}
                    className={`px-2 py-0.5 rounded-full text-xs font-bold shadow transition-colors ${
                      zoom === z ? "text-white" : "bg-white/90 text-gray-700 hover:bg-white"
                    }`}
                    style={zoom === z ? { backgroundColor: 'rgb(var(--brand-color))' } : {}}
                  >
                    {z === 1 ? "1×" : `${z}×`}
                  </button>
                ))}
              </div>
            )}

            {displayImages.length > 1 && (
              <>
                <button
                  onClick={() => setActiveIndex((i) => (i - 1 + displayImages.length) % displayImages.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setActiveIndex((i) => (i + 1) % displayImages.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {displayImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${i === activeIndex ? "w-4" : "w-1.5 bg-white/70"}`}
                      style={i === activeIndex ? { backgroundColor: 'rgb(var(--brand-color))' } : {}}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {zoom > 1 && lensState && !isVideo(displayImages[activeIndex]) && (() => {
            const mx = lensState.clientX - lensState.rect.left;
            const my = lensState.clientY - lensState.rect.top;
            const bgW = lensState.rect.width * zoom;
            const bgH = lensState.rect.height * zoom;
            const bgX = LENS_SIZE / 2 - mx * zoom;
            const bgY = LENS_SIZE / 2 - my * zoom;
            return (
              <div
                style={{
                  position: "fixed",
                  width: LENS_SIZE,
                  height: LENS_SIZE,
                  left: lensState.clientX - LENS_SIZE / 2,
                  top: lensState.clientY - LENS_SIZE / 2,
                  borderRadius: "50%",
                  border: "2px solid white",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                  backgroundImage: `url("${resolveAssetUrl(displayImages[activeIndex])}")`,
                  backgroundSize: `${bgW}px ${bgH}px`,
                  backgroundPosition: `${bgX}px ${bgY}px`,
                  backgroundRepeat: "no-repeat",
                  pointerEvents: "none",
                  zIndex: 9999,
                }}
              />
            );
          })()}

          {displayImages.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {displayImages.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeIndex ? "" : "border-transparent hover:border-gray-300"}`}
                  style={i === activeIndex ? { borderColor: 'rgb(var(--brand-color))' } : {}}
                >
                  {isVideo(url) ? (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  ) : (
                    <img src={resolveAssetUrl(url)} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-5">
          {product.categoryName && (
            <Link
              to={`/shop?category=${product.categorySlug}`}
              className="text-sm font-bold uppercase tracking-widest hover:opacity-70"
              style={{ color: 'rgb(var(--brand-color))' }}
            >
              {product.categoryName}
            </Link>
          )}

          <h1 className="text-3xl font-bold text-gray-900 leading-tight">
            {productName}
          </h1>

          <p className="text-2xl font-bold" style={{ color: 'rgb(var(--brand-color))' }}>
            {formatPrice(getPriceForSize(product, selectedSize, currency), currency)}
          </p>

          <p className="text-gray-600 leading-relaxed">{productDescription}</p>

          {tenantId === "chlorine.art" && (
            <div className="flex flex-col gap-4 py-4 border-y border-gray-100">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <span className="p-1 rounded bg-gray-900 text-white text-[10px] uppercase tracking-widest">{t("product.premiumBadge")}</span>
                {t("product.certOfAuthenticity")}
              </div>
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("product.printSpecsTitle")}</h4>
                <p className="text-sm text-gray-600">{t("product.printSpecsDesc")}</p>
              </div>
            </div>
          )}

          {product.sizePrices && product.sizePrices.length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">{t("product.printSize")}</label>
              <div className="flex gap-2 flex-wrap">
                {PRINT_SIZES.filter((sz) => product.sizePrices!.some((p) => p.size === sz)).map((sz) => (
                  <button
                    key={sz}
                    onClick={() => setSelectedSize(sz)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${selectedSize === sz ? "text-white" : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"}`}
                    style={selectedSize === sz ? { backgroundColor: 'rgb(var(--brand-color))', borderColor: 'rgb(var(--brand-color))' } : {}}
                  >
                    {SIZE_LABEL[sz] ?? sz}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-2">
            <label className="text-sm font-medium text-gray-700">{tTenant("product.qty")}</label>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-gray-600 hover:bg-gray-50 transition-colors font-bold">−</button>
              <span className="px-4 py-2 text-sm font-semibold w-10 text-center">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="px-3 py-2 text-gray-600 hover:bg-gray-50 transition-colors font-bold">+</button>
            </div>
          </div>

          <button
            onClick={handleAddToCart}
            disabled={product.soldOut || product.stock === 0 || siteMode === "read_only"}
            className={`w-full mt-2 btn-primary ${product.soldOut || product.stock === 0 || siteMode === "read_only" ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <ShoppingCart size={18} />
            {product.soldOut ? "Sold Out" : product.stock === 0 ? tTenant("product.outOfStock") : siteMode === "read_only" ? "Orders Disabled" : tTenant("product.addToCart")}
          </button>

          <div className="rounded-xl p-4 text-sm space-y-1.5" style={{ backgroundColor: 'rgba(var(--brand-color), 0.05)', color: 'rgb(var(--brand-color))' }}>
            <p>🖨️ {tTenant("product.printedIn")}</p>
            <p>🚚 {tTenant("product.ships")}</p>
            <p>💳 {tTenant("product.secure")}</p>
            <p>↩️ {tTenant("product.returns")}</p>
          </div>
        </div>
      </div>
    </main>
  );
}
